//src/router/index.ts

import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw
} from 'vue-router'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { auth } from '@/firebase'
import { UseCustomerStore } from '@/stores/CustomerStore'
import { Roles } from '@/types/Helper/Enums'
import { INSTRUCTOR_ROLES, MAINTENANCE_STAFF_ROLES } from '@/utils/signingPermissions'
import { Module } from '@/types/Store/StoreEnums'
import { watch } from 'vue'

export enum RouteNames {
  SignIn = 'sign-in',
  AccountReactivation = 'account-reactivation',
  Home = 'home',
  HomeDashboard = 'home-dashboard',
  HomeProfile = 'home-profile',
  HomeAircraft = 'home-aircraft',
  HomeAccounts = 'home-accounts',
  HomeUserDirectory = 'home-user-directory',
  HomeClients = 'home-clients',
  HomeSchedule = 'home-schedule',
  HomeLeads = 'home-leads',
  HomeFinance = 'home-finance',
  HomeMaintenanceDashboard = 'maintenance-dashboard',
  HomeMaintenanceSquawks = 'maintenance-squawks',
  HomeMaintenanceEquipment = 'maintenance-equipment',
  HomeReports = 'home-reports',
  HomeRooms = 'home-rooms',
  HomeStoreSettings = 'home-store-settings',
  HomeUserSettings = 'home-user-settings',
  HomePart141Student = 'home-part141-student',
  HomePart141Admin = 'home-part141-admin',
  HomePart141Documents = 'home-part141-documents',
  BuildLessonUI = 'build-lesson-ui',
  About = 'about',
  Pricing = 'pricing',
  CheckRides = 'check-rides',
  Examiner = 'examiner',
  PaymentServices = 'payment-services',
  //BuildLessonUI = 'BuildLessonGradesheetTable',
  InvoiceBuilder = 'invoice-builder',
  InvoiceList = 'invoice-list',
  SalesTaxReport = 'sales-tax-report',
  HomeFueling = 'home-fueling',
  HomeFuelingAdmin = 'home-fueling-admin',
  HomeFuelingReport = 'Home-Fueling-Report',
  HomeFuelReceipts = 'home-fuel-receipts',
  HomeMaintenanceTemplates = 'maintenance-templates',
  HomeMaintenanceAdCai = 'maintenance-adcai',
  HomeMaintenanceWorkOrders = 'maintenance-work-orders',
  DevModules = 'dev-modules'
}

export enum RoutePaths {
  SignIn = '/',
  AccountReactivation = '/reactivate',
  Home = '/home',
  About = '/about',
  Pricing = '/pricing',
  CheckRides = '/check-rides',
  Examiner = '/examiner',
  PaymentServices = '/payment-services',
  BuildLessonUI = '/build-lesson-ui',
  InvoiceBuilder = '/invoice-builder',
  InvoiceList = '/invoices',
  SalesTaxReport = '/sales-tax-report',
  HomeFueling = '/fueling',
  HomeFuelingAdmin = '/fueling/admin',
  HomeFuelingReport = '/fueling/report'
}

type AccessRouteMeta = {
  requiredRoles?: Roles[]
  requiredModules?: Module[]
}

const routes: RouteRecordRaw[] = [
  {
    path: RoutePaths.SignIn,
    name: RouteNames.SignIn,
    component: () => import('@/views/SignInView.vue')
  },
  {
    path: RoutePaths.Home,
    component: () => import('@/views/HomeView.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: RouteNames.Home,
        redirect: { name: RouteNames.HomeDashboard }
      },
      {
        path: 'dashboard',
        name: RouteNames.HomeDashboard,
        component: () => import('@/components/TopLevelComponents/DashboardComponent.vue')
      },
      {
        path: 'profile',
        name: RouteNames.HomeProfile,
        component: () => import('@/components/TopLevelComponents/ProfileComponent.vue')
      },
      {
        path: 'aircraft',
        name: RouteNames.HomeAircraft,
        component: () => import('@/components/TopLevelComponents/AircraftAdminComponent.vue')
      },
      {
        path: 'accounts',
        name: RouteNames.HomeAccounts,
        component: () => import('@/components/TopLevelComponents/AccountAdminComponent.vue'),
        meta: { requiredRoles: [Roles.Admin, Roles.HeadInstructor] }
      },
      {
        path: 'user-directory',
        name: RouteNames.HomeUserDirectory,
        component: () => import('@/components/TopLevelComponents/UserDirectoryComponent.vue')
      },
      {
        path: 'external-accounts',
        name: RouteNames.HomeClients,
        component: () => import('@/components/TopLevelComponents/ClientComponent.vue'),
        meta: {
          requiredRoles: [Roles.Admin, Roles.HeadInstructor],
          requiredModules: [Module.Client]
        }
      },
      {
        path: 'schedule',
        name: RouteNames.HomeSchedule,
        component: () => import('@/components/TopLevelComponents/ScheduleComponent.vue'),
        meta: { requiredModules: [Module.Scheduling] }
      },
      {
        path: 'leads',
        name: RouteNames.HomeLeads,
        component: () => import('@/components/TopLevelComponents/LeadsComponent.vue'),
        meta: {
          requiredRoles: [Roles.Admin, Roles.HeadInstructor],
          requiredModules: [Module.Leads]
        }
      },
      {
        path: 'finance',
        name: RouteNames.HomeFinance,
        component: () => import('@/components/TopLevelComponents/LedgerView.vue'),
        meta: { requiredModules: [Module.Finance] }
      },
      {
        path: 'maintenance',
        name: RouteNames.HomeMaintenanceDashboard,
        component: () => import('@/components/Maintenance/MaintenanceComponent.vue'),
        // Maintenance Tech and every mechanic role (utils/signingPermissions.ts).
        meta: { requiredRoles: [...MAINTENANCE_STAFF_ROLES], requiredModules: [Module.Maintenance] }
      },
      {
        path: 'maintenance/squawks',
        name: RouteNames.HomeMaintenanceSquawks,
        component: () => import('@/components/Maintenance/SquawksComponent.vue'),
        meta: { requiredRoles: [...MAINTENANCE_STAFF_ROLES], requiredModules: [Module.Maintenance] }
      },
      {
        path: 'templates',
        name: RouteNames.HomeMaintenanceTemplates,
        component: () => import('@/components/Maintenance/InspectionTemplateEditor.vue'),
        // Same as the sidebar item: Admin or Maintenance Tech.
        meta: {
          requiredRoles: [Roles.Admin, Roles.Maintenance],
          requiredModules: [Module.Maintenance]
        }
      },
      {
        path: 'maintenance/adcai',
        name: RouteNames.HomeMaintenanceAdCai,
        component: () => import('@/components/Maintenance/AdCaiEditor.vue'),
        // Maintenance setup: Maintenance Tech only (not mechanics).
        meta: { requiredRoles: [Roles.Maintenance], requiredModules: [Module.Maintenance] }
      },
      {
        // Work Orders module (needs Maintenance too). Maintenance staff work
        // them; Owner/Admin can view. :workOrderId opens one.
        path: 'maintenance/work-orders/:workOrderId?',
        name: RouteNames.HomeMaintenanceWorkOrders,
        component: () => import('@/components/Maintenance/WorkOrders/WorkOrdersComponent.vue'),
        meta: {
          requiredRoles: [...MAINTENANCE_STAFF_ROLES, Roles.Admin, Roles.StoreOwner],
          requiredModules: [Module.Maintenance, Module.WorkOrders]
        }
      },
      {
        path: 'maintenance/equipment',
        name: RouteNames.HomeMaintenanceEquipment,
        component: () => import('@/components/Maintenance/MaintenanceEquipmentComponent.vue'),
        meta: {
          requiredRoles: [Roles.Maintenance],
          requiredModules: [Module.Maintenance, Module.Equipment]
        }
      },
      {
        path: 'reports',
        name: RouteNames.HomeReports,
        component: () => import('@/components/TopLevelComponents/ReportsComponent.vue'),
        meta: {
          requiredRoles: [Roles.Admin, Roles.HeadInstructor],
          requiredModules: [Module.Reports]
        }
      },
      {
        path: 'fuel-receipts',
        name: RouteNames.HomeFuelReceipts,
        component: () => import('@/components/FuelReceipts/FuelReceiptsPage.vue'),
        meta: { requiredModules: [Module.FuelReceipts] }
      },
      {
        path: 'part141',
        meta: { requiredModules: [Module.Part141] },
        children: [
          {
            path: '',
            redirect: { name: RouteNames.HomePart141Student }
          },
          {
            path: 'student',
            name: RouteNames.HomePart141Student,
            component: () => import('@/components/Part141/Part141StudentComponent.vue'),
            // Same list as the sidebar: Admin, Student and any instructor (CFI
            // included). Changing Part 141 records also needs the Part 141 role
            // (utils/signingPermissions.ts).
            meta: { requiredRoles: [Roles.Admin, Roles.Student, ...INSTRUCTOR_ROLES] }
          },
          {
            path: 'admin',
            name: RouteNames.HomePart141Admin,
            component: () => import('@/components/Part141/Part141AdminComponent.vue'),
            meta: { requiredRoles: [Roles.Admin, Roles.ChiefPilot, Roles.asstchiefPilot] }
          },
          {
            path: 'documents',
            name: RouteNames.HomePart141Documents,
            component: () => import('@/components/Part141/Part141Documents.vue'),
            meta: { requiredRoles: [Roles.Admin, Roles.ChiefPilot, Roles.asstchiefPilot] }
          },
          {
            path: 'tco-builder',
            name: RouteNames.BuildLessonUI,
            component: () => import('@/components/Part141/BuildLessonGradeSheetTable.vue'),
            meta: { requiredRoles: [Roles.Admin, Roles.ChiefPilot, Roles.asstchiefPilot] }
          }
        ]
      },
      {
        path: 'rooms',
        name: RouteNames.HomeRooms,
        component: () => import('@/components/TopLevelComponents/RoomsComponent.vue')
      },
      {
        path: 'store-settings',
        name: RouteNames.HomeStoreSettings,
        component: () => import('@/components/TopLevelComponents/StoreSettingsComponent.vue'),
        meta: { requiredRoles: [Roles.Admin, Roles.Dev] }
      },
      {
        path: 'dev/modules',
        name: RouteNames.DevModules,
        component: () => import('@/components/Dev/DevModulesComponent.vue'),
        meta: { requiredRoles: [Roles.Dev] }
      },
      {
        path: 'user-settings',
        name: RouteNames.HomeUserSettings,
        component: () => import('@/components/TopLevelComponents/UserSettingsComponent.vue')
      },
      // ─── NEW: Invoice builder — opens in a new tab, lives under /home ──────
      // Full path will be /home/invoices/:invoiceId
      // Rendered without the app shell nav so it fills the viewport cleanly.
      {
        path: 'invoices/:invoiceId',
        name: RouteNames.InvoiceBuilder,
        component: () => import('@/components/Invoices/InvoiceBuilderPage.vue'),
        meta: { requiredModules: [Module.Finance] }
      },
      // ───────────────────────────────────────────────────────────────────────
      {
        path: RoutePaths.InvoiceList,
        name: RouteNames.InvoiceList,
        component: () => import('@/components/Invoices/InvoiceListPage.vue'),
        meta: { requiredModules: [Module.Finance] }
      },
      {
        path: 'tax-report',
        name: RouteNames.SalesTaxReport,
        component: () => import('@/components/Finance/SalesTaxReportPage.vue'),
        meta: { requiredModules: [Module.Finance] }
      },
      {
        path: 'Fueling',
        name: RouteNames.HomeFueling,
        component: () => import('@/components/Fueling/FuelPage.vue'),
        meta: {
          requiredRoles: [Roles.Admin, Roles.FuelAdmin, Roles.Fueler],
          requiredModules: [Module.Fueling]
        }
      },
      {
        path: 'Fueling/admin',
        name: RouteNames.HomeFuelingAdmin,
        component: () => import('@/components/Fueling/FuelTruckAdminComponent.vue'),
        meta: { requiredRoles: [Roles.Admin, Roles.FuelAdmin], requiredModules: [Module.Fueling] }
      },
      {
        path: 'Fueling/report',
        name: RouteNames.HomeFuelingReport,
        component: () => import('@/components/Fueling/FuelReportComponent.vue'),
        meta: { requiredRoles: [Roles.Admin, Roles.FuelAdmin], requiredModules: [Module.Fueling] }
      }
    ]
  },
  {
    path: RoutePaths.AccountReactivation,
    name: RouteNames.AccountReactivation,
    component: () => import('@/views/AccountReactivationView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: RoutePaths.About,
    name: RouteNames.About,
    component: () => import('@/views/AboutView.vue')
  },
  {
    // Public (no sign-in): applicants submit a Practical Test Application.
    // /check-rides → the information page (get the link from your examiner);
    // /check-rides/{examiner} → that examiner's page.
    path: `${RoutePaths.CheckRides}/:examinerId?`,
    name: RouteNames.CheckRides,
    component: () => import('@/views/CheckRidesView.vue')
  },
  {
    path: RoutePaths.Pricing,
    name: RouteNames.Pricing,
    component: () => import('@/views/PricingView.vue')
  },
  {
    path: RoutePaths.Examiner,
    name: RouteNames.Examiner,
    component: () => import('@/views/ExaminerView.vue'),
    meta: { examinerService: true }
  },
  {
    path: RoutePaths.PaymentServices,
    name: RouteNames.PaymentServices,
    component: () => import('@/views/PaymentServicesView.vue')
  },
  {
    path: `${RoutePaths.PaymentServices}/:storeCode`,
    name: 'store-payment-services',
    component: () => import('@/views/PaymentServicesView.vue')
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: RouteNames.SignIn }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

const authReady = new Promise<User | null>((resolve, reject) => {
  const unsubscribe = onAuthStateChanged(
    auth,
    (user) => {
      unsubscribe()
      resolve(user)
    },
    reject
  )
})

function collectAccessMeta(to: RouteLocationNormalized): AccessRouteMeta {
  return to.matched.reduce<AccessRouteMeta>(
    (access, route) => {
      const meta = route.meta as AccessRouteMeta

      return {
        requiredRoles: [...(access.requiredRoles ?? []), ...(meta.requiredRoles ?? [])],
        requiredModules: [...(access.requiredModules ?? []), ...(meta.requiredModules ?? [])]
      }
    },
    {
      requiredRoles: [],
      requiredModules: []
    }
  )
}

function waitForStore(storeCode: string, timeoutMs = 8000): Promise<void> {
  const sessionData = UseCustomerStore()

  if (sessionData.currentStore?.storeCode === storeCode) {
    return Promise.resolve()
  }

  // fetchStore() kicks off its own onSnapshot listener and populates
  // currentStore asynchronously — start it if we're not already on this
  // store, then wait for currentStore to catch up.
  sessionData.fetchStore(storeCode)

  return new Promise((resolve) => {
    let settled = false

    const stop = watch(
      () => sessionData.currentStore?.storeCode,
      (code) => {
        if (code === storeCode && !settled) {
          settled = true
          stop()
          resolve()
        }
      }
    )

    setTimeout(() => {
      if (!settled) {
        settled = true
        stop()
        console.warn(`waitForStore: timed out waiting for store "${storeCode}"`)
        resolve()
      }
    }, timeoutMs)
  })
}

router.beforeEach(async (to) => {
  await authReady

  const user = auth.currentUser

  if (to.matched.some((route) => route.meta.requiresAuth) && !user) {
    return { name: RouteNames.SignIn }
  }

  if (to.name === RouteNames.SignIn && user) {
    return { name: RouteNames.HomeDashboard }
  }

  const sessionData = UseCustomerStore()
  const account = sessionData.currentUser

  // Multiple-school accounts choose a school explicitly on Home. Only a
  // single-school account has an unambiguous default for the activation check.
  const activeStoreCode =
    sessionData.selectedStoreCode ||
    (account?.storeName.length === 1 ? account.getPrimaryStoreName() : '')

  // Losing activation affects the selected school. Public pages remain
  // available, and the reactivation page offers an explicit school switch.
  if (user && account && !account.isDev() && to.matched.some((route) => route.meta.requiresAuth)) {
    // Check Active first: an inactive member can't load the school (the rules
    // allow only active members), so waiting for it would just time out.
    if (
      activeStoreCode &&
      account.hasStore(activeStoreCode) &&
      !account.getActive(activeStoreCode)
    ) {
      if (to.name !== RouteNames.AccountReactivation) {
        return { name: RouteNames.AccountReactivation }
      }
      return true
    }

    if (sessionData.selectedStoreCode) await waitForStore(activeStoreCode)
  }

  if (
    account &&
    (account.isDev() || account.getActive(activeStoreCode)) &&
    to.name === RouteNames.AccountReactivation
  ) {
    // Active accounts/devs have no reason to be here.
    return { name: RouteNames.HomeDashboard }
  }

  const { requiredRoles = [], requiredModules = [] } = collectAccessMeta(to)
  if (requiredRoles.length || requiredModules.length) {
    if (!sessionData.currentUser || !sessionData.currentStore) {
      return { name: RouteNames.HomeDashboard }
    }

    const selectedStoreCode = sessionData.selectedStoreCode
    const hasRequiredRoles =
      requiredRoles.length === 0 ||
      (sessionData.currentUser?.hasRoles?.(requiredRoles, selectedStoreCode) ?? false)
    const hasRequiredModules = requiredModules.every((module) =>
      sessionData.currentStore?.hasModule(module)
    )

    if (!hasRequiredRoles || !hasRequiredModules) {
      return { name: RouteNames.HomeDashboard }
    }
  }

  return true
})

export default router
